import React, { useRef } from 'react';
import IconButton from '@frontend/Components/Inputs/IconButton';
import type { ToggleElement as ToggleElementType } from '@frontend/Models/TopBar';
import './ToggleElement.css';

/**
 * ToggleElement - Toggle button group component for TopBar
 * 
 * This component creates a group of toggle buttons (e.g., My/All Matters)
 * using the existing IconButton component. It manages active state and
 * provides keyboard navigation between options.
 * 
 * @param props - ToggleElement configuration from TopBar
 */
const ToggleElement: React.FC<ToggleElementType> = ({
  options,
  value,
  onChange,
  condition
}) => {
  const toggleGroupRef = useRef<HTMLDivElement>(null);

  // Handle conditional rendering
  if (condition && !condition()) {
    return null;
  }

  // Handle keyboard navigation (arrow keys)
  const handleKeyDown = (event: React.KeyboardEvent, currentIndex: number) => {
    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      nextIndex = (currentIndex + 1) % options.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      nextIndex = currentIndex === 0 ? options.length - 1 : currentIndex - 1;
    } else {
      return; // Not an arrow key, don't do anything
    }

    // Focus the next button
    const buttons = toggleGroupRef.current?.querySelectorAll('button');
    if (buttons && buttons[nextIndex]) {
      (buttons[nextIndex] as HTMLButtonElement).focus();
    }

    // Also update the value
    onChange(options[nextIndex].value);
  };

  return (
    <div
      ref={toggleGroupRef}
      className="topBar-toggle"
      role="radiogroup"
      aria-label="Toggle options"
    >
      {options.map((option, index) => {
        const isActive = option.value === value;

        return (
          <IconButton
            key={index}
            icon={option.icon}
            ariaLabel={option.ariaLabel}
            onClick={() => onChange(option.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className="topBar-toggle-option"
            style={{
              backgroundColor: isActive ? '#FFFFFF' : 'transparent',
              transition: 'background-color 0.2s ease'
            }}
            aria-checked={isActive}
            role="radio"
          />
        );
      })}
    </div>
  );
};

export default ToggleElement;

